/**
 * Student Learning Profile (SLP) Service
 * Handles all SLP-related operations including activity management, 
 * student records, calendar events, and learning profile tracking
 */

import { supabaseClient, supabaseAdmin } from '../../supabase/clients.js';

class SlpService {
  constructor() {
    this.client = supabaseClient;
    this.admin = supabaseAdmin;
  }

  /**
   * Get all students for SLP system
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudents(tenantId = 'tn_01') {
    try {
      // Try to get real student data
      try {
        const { data, error } = await this.client
          .from('saas_students')
          .select(`
            id,
            student_name,
            student_id,
            class_id,
            gender,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true })
          .order('student_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_students table not found, generating mock data');
      }

      // Fallback: Generate mock student data
      return this.generateMockStudents();
    } catch (error) {
      console.error('Error fetching students:', error);
      return this.generateMockStudents();
    }
  }

  /**
   * Get all teachers for SLP system
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of teacher objects
   */
  async getTeachers(tenantId = 'tn_01') {
    try {
      // Try to get real teacher data
      try {
        const { data, error } = await this.client
          .from('saas_teachers')
          .select(`
            id,
            teacher_name,
            teacher_id,
            email,
            phone,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('teacher_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_teachers table not found, generating mock data');
      }

      // Fallback: Generate mock teacher data
      return this.generateMockTeachers();
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return this.generateMockTeachers();
    }
  }

  /**
   * Get all SLP activities
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of activity objects
   */
  async getActivities(tenantId = 'tn_01') {
    try {
      // Try to get real activity data
      try {
        const { data, error } = await this.client
          .from('saas_slp_activities')
          .select(`
            id,
            activity_name,
            activity_name_en,
            activity_description,
            activity_category,
            activity_date,
            start_time,
            end_time,
            duration_hours,
            location,
            organizer,
            participant_count,
            target_grades,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('activity_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_slp_activities table not found, generating mock data');
      }

      // Fallback: Generate mock activity data
      return this.generateMockActivities();
    } catch (error) {
      console.error('Error fetching activities:', error);
      return this.generateMockActivities();
    }
  }

  /**
   * Get student participation records
   * @param {string} studentId - Student identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of participation records
   */
  async getStudentRecords(studentId, tenantId = 'tn_01') {
    try {
      // Try to get real participation data
      try {
        const { data, error } = await this.client
          .from('saas_slp_participations')
          .select(`
            id,
            student_id,
            activity_id,
            participation_date,
            achievement_level,
            notes,
            tenant_id,
            created_at,
            updated_at,
            saas_slp_activities!inner(
              id,
              activity_name,
              activity_category,
              activity_date,
              location,
              organizer
            )
          `)
          .eq('student_id', studentId)
          .eq('tenant_id', tenantId)
          .order('participation_date', { ascending: false });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_slp_participations table not found, generating mock data');
      }

      // Fallback: Generate mock participation data
      return this.generateMockStudentRecords(studentId);
    } catch (error) {
      console.error('Error fetching student records:', error);
      return this.generateMockStudentRecords(studentId);
    }
  }

  /**
   * Get activities by date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of activity objects
   */
  async getActivitiesByDateRange(startDate, endDate, tenantId = 'tn_01') {
    try {
      // Try to get real activity data
      try {
        const { data, error } = await this.client
          .from('saas_slp_activities')
          .select(`
            id,
            activity_name,
            activity_name_en,
            activity_description,
            activity_category,
            activity_date,
            start_time,
            end_time,
            duration_hours,
            location,
            organizer,
            participant_count,
            target_grades,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .gte('activity_date', startDate)
          .lte('activity_date', endDate)
          .order('activity_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_slp_activities table not found, generating mock data');
      }

      // Fallback: Generate mock activity data for date range
      return this.generateMockActivitiesForDateRange(startDate, endDate);
    } catch (error) {
      console.error('Error fetching activities by date range:', error);
      return this.generateMockActivitiesForDateRange(startDate, endDate);
    }
  }

  /**
   * Get SLP statistics
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Statistics object
   */
  async getSlpStats(tenantId = 'tn_01') {
    try {
      const [students, teachers, activities] = await Promise.all([
        this.getStudents(tenantId),
        this.getTeachers(tenantId),
        this.getActivities(tenantId)
      ]);

      const totalStudents = students.length;
      const totalTeachers = teachers.length;
      const totalActivities = activities.length;
      const totalParticipations = activities.reduce((sum, activity) => sum + (activity.participant_count || 0), 0);

      // Category distribution
      const categoryStats = {};
      activities.forEach(activity => {
        const category = activity.activity_category;
        categoryStats[category] = (categoryStats[category] || 0) + 1;
      });

      return {
        totalStudents,
        totalTeachers,
        totalActivities,
        totalParticipations,
        categoryStats,
        gradeDistribution: this.getGradeDistribution(students),
        monthlyActivityCount: this.getMonthlyActivityCount(activities)
      };
    } catch (error) {
      console.error('Error fetching SLP stats:', error);
      return {
        totalStudents: 0,
        totalTeachers: 0,
        totalActivities: 0,
        totalParticipations: 0,
        categoryStats: {},
        gradeDistribution: {},
        monthlyActivityCount: {}
      };
    }
  }

  /**
   * Add new activity
   * @param {Object} activityData - Activity data object
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Created activity object
   */
  async addActivity(activityData, tenantId = 'tn_01') {
    try {
      // Try to save to database
      try {
        const { data, error } = await this.client
          .from('saas_slp_activities')
          .insert([{
            ...activityData,
            tenant_id: tenantId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_slp_activities table not found, saving to localStorage');
      }

      // Fallback: Save to localStorage
      const activities = JSON.parse(localStorage.getItem('slp_activities') || '[]');
      const newActivity = {
        id: `activity_${Date.now()}`,
        ...activityData,
        tenant_id: tenantId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      activities.push(newActivity);
      localStorage.setItem('slp_activities', JSON.stringify(activities));
      return newActivity;
    } catch (error) {
      console.error('Error adding activity:', error);
      throw error;
    }
  }

  /**
   * Update existing activity
   * @param {string} activityId - Activity identifier
   * @param {Object} activityData - Updated activity data
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Updated activity object
   */
  async updateActivity(activityId, activityData, tenantId = 'tn_01') {
    try {
      // Try to update in database
      try {
        const { data, error } = await this.client
          .from('saas_slp_activities')
          .update({
            ...activityData,
            updated_at: new Date().toISOString()
          })
          .eq('id', activityId)
          .eq('tenant_id', tenantId)
          .select()
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_slp_activities table not found, updating localStorage');
      }

      // Fallback: Update in localStorage
      const activities = JSON.parse(localStorage.getItem('slp_activities') || '[]');
      const index = activities.findIndex(activity => activity.id === activityId);
      if (index !== -1) {
        activities[index] = {
          ...activities[index],
          ...activityData,
          updated_at: new Date().toISOString()
        };
        localStorage.setItem('slp_activities', JSON.stringify(activities));
        return activities[index];
      }
      throw new Error('Activity not found');
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  }

  /**
   * Delete activity
   * @param {string} activityId - Activity identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<boolean>} Success status
   */
  async deleteActivity(activityId, tenantId = 'tn_01') {
    try {
      // Try to delete from database
      try {
        const { error } = await this.client
          .from('saas_slp_activities')
          .delete()
          .eq('id', activityId)
          .eq('tenant_id', tenantId);

        if (!error) {
          return true;
        }
      } catch (tableError) {
        console.log('saas_slp_activities table not found, deleting from localStorage');
      }

      // Fallback: Delete from localStorage
      const activities = JSON.parse(localStorage.getItem('slp_activities') || '[]');
      const filteredActivities = activities.filter(activity => activity.id !== activityId);
      localStorage.setItem('slp_activities', JSON.stringify(filteredActivities));
      return true;
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  }

  // Helper methods for statistics
  getGradeDistribution(students) {
    const distribution = {};
    students.forEach(student => {
      const grade = student.class_id ? student.class_id.charAt(0) : 'Unknown';
      distribution[grade] = (distribution[grade] || 0) + 1;
    });
    return distribution;
  }

  getMonthlyActivityCount(activities) {
    const monthlyCount = {};
    activities.forEach(activity => {
      const month = activity.activity_date ? activity.activity_date.substring(0, 7) : 'Unknown';
      monthlyCount[month] = (monthlyCount[month] || 0) + 1;
    });
    return monthlyCount;
  }

  // Mock data generation methods
  generateMockStudents() {
    const students = [
      // Form 6A students
      { name: '陳綺婷', id: '6A001', class: '6A', gender: 'F' },
      { name: '錢嘉欣', id: '6A002', class: '6A', gender: 'F' },
      { name: '李嘉蔚', id: '6A003', class: '6A', gender: 'F' },
      { name: '張志明', id: '6A004', class: '6A', gender: 'M' },
      { name: '王美玲', id: '6A005', class: '6A', gender: 'F' },
      { name: '劉建華', id: '6A006', class: '6A', gender: 'M' },
      { name: '黃淑芬', id: '6A007', class: '6A', gender: 'F' },
      { name: '林志強', id: '6A008', class: '6A', gender: 'M' },
      { name: '吳雅婷', id: '6A009', class: '6A', gender: 'F' },
      { name: '鄭文傑', id: '6A010', class: '6A', gender: 'M' },
      
      // Form 5 students
      { name: '周美惠', id: '5A001', class: '5A', gender: 'F' },
      { name: '蔡志明', id: '5A002', class: '5A', gender: 'M' },
      { name: '許淑芬', id: '5A003', class: '5A', gender: 'F' },
      { name: '謝建華', id: '5A004', class: '5A', gender: 'M' },
      { name: '羅雅婷', id: '5A005', class: '5A', gender: 'F' },
      { name: '葉志強', id: '5A006', class: '5A', gender: 'M' },
      { name: '何美玲', id: '5A007', class: '5A', gender: 'F' },
      { name: '高建民', id: '5A008', class: '5A', gender: 'M' },
      { name: '梁淑娟', id: '5A009', class: '5A', gender: 'F' },
      { name: '蘇志豪', id: '5A010', class: '5A', gender: 'M' },
      
      // Form 4 students
      { name: '范美惠', id: '4A001', class: '4A', gender: 'F' },
      { name: '江文傑', id: '4A002', class: '4A', gender: 'M' },
      { name: '方雅婷', id: '4A003', class: '4A', gender: 'F' },
      { name: '石志強', id: '4A004', class: '4A', gender: 'M' },
      { name: '余美玲', id: '4A005', class: '4A', gender: 'F' },
      { name: '鄧建華', id: '4A006', class: '4A', gender: 'M' },
      { name: '薛淑芬', id: '4A007', class: '4A', gender: 'F' },
      { name: '韓志明', id: '4A008', class: '4A', gender: 'M' },
      { name: '馮雅文', id: '4A009', class: '4A', gender: 'F' },
      { name: '曾志豪', id: '4A010', class: '4A', gender: 'M' }
    ];

    return students.map((student, index) => ({
      id: `student_${student.id}`,
      student_name: student.name,
      student_id: student.id,
      class_id: student.class,
      gender: student.gender,
      tenant_id: 'tn_01',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  }

  generateMockTeachers() {
    const teachers = [
      { name: 'Ms. Sarah Johnson', id: 'T001', email: 'sarah.johnson@school.edu.hk' },
      { name: 'Mr. David Chen', id: 'T002', email: 'david.chen@school.edu.hk' },
      { name: 'Ms. Lisa Wong', id: 'T003', email: 'lisa.wong@school.edu.hk' },
      { name: 'Mr. Michael Lee', id: 'T004', email: 'michael.lee@school.edu.hk' },
      { name: 'Ms. Jennifer Liu', id: 'T005', email: 'jennifer.liu@school.edu.hk' },
      { name: 'Mr. Robert Zhang', id: 'T006', email: 'robert.zhang@school.edu.hk' },
      { name: 'Ms. Emily Wang', id: 'T007', email: 'emily.wang@school.edu.hk' },
      { name: 'Mr. James Liu', id: 'T008', email: 'james.liu@school.edu.hk' }
    ];

    return teachers.map((teacher, index) => ({
      id: `teacher_${teacher.id}`,
      teacher_name: teacher.name,
      teacher_id: teacher.id,
      email: teacher.email,
      phone: `+852-${Math.floor(Math.random() * 90000000) + 20000000}`,
      tenant_id: 'tn_01',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  }

  generateMockActivities() {
    const activities = [];
    const categories = [
      { name: '學術', name_en: 'Academic', color: 'blue', icon: '📚' },
      { name: '體育', name_en: 'Sports', color: 'green', icon: '🏃' },
      { name: '藝術', name_en: 'Arts', color: 'purple', icon: '🎨' },
      { name: '社會服務', name_en: 'Community Service', color: 'yellow', icon: '🤝' },
      { name: '領袖訓練', name_en: 'Leadership Training', color: 'red', icon: '👑' },
      { name: '考試', name_en: 'Examination', color: 'darkred', icon: '📝' },
      { name: '假期', name_en: 'Holiday', color: 'darkgreen', icon: '🎊' },
      { name: '典禮', name_en: 'Ceremony', color: 'darkblue', icon: '🏛️' },
      { name: '競賽', name_en: 'Competition', color: 'orange', icon: '🏆' }
    ];

    const activityNames = {
      '學術': ['數學競賽', '科學展覽', '英語演講比賽', '歷史研究報告'],
      '體育': ['田徑比賽', '籃球聯賽', '游泳比賽', '足球友誼賽'],
      '藝術': ['音樂會', '美術展覽', '戲劇表演', '舞蹈比賽'],
      '社會服務': ['義工服務', '社區清潔', '老人院探訪', '環保活動'],
      '領袖訓練': ['學生會培訓', '班長訓練', '領袖營', '團隊建設'],
      '考試': ['期中考試', '期末考試', '模擬考試', '小測驗'],
      '假期': ['聖誕假期', '新年假期', '復活節假期', '暑假'],
      '典禮': ['開學典禮', '畢業典禮', '頒獎典禮', '校慶典禮'],
      '競賽': ['辯論比賽', '奧數競賽', '科學競賽', '創意比賽']
    };

    // Generate activities for the current year
    const currentYear = new Date().getFullYear();
    const months = [1, 2, 3, 4, 5, 6, 9, 10, 11, 12]; // School months

    months.forEach(month => {
      const daysInMonth = new Date(currentYear, month, 0).getDate();
      const activitiesPerMonth = Math.floor(Math.random() * 8) + 5; // 5-12 activities per month

      for (let i = 0; i < activitiesPerMonth; i++) {
        const day = Math.floor(Math.random() * daysInMonth) + 1;
        const category = categories[Math.floor(Math.random() * categories.length)];
        const activityName = activityNames[category.name][Math.floor(Math.random() * activityNames[category.name].length)];
        
        const activityDate = `${currentYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        const startTime = `${Math.floor(Math.random() * 8) + 8}:${Math.floor(Math.random() * 4) * 15}`;
        const endTime = `${Math.floor(Math.random() * 4) + parseInt(startTime.split(':')[0]) + 1}:${Math.floor(Math.random() * 4) * 15}`;
        
        activities.push({
          id: `activity_${currentYear}_${month}_${day}_${i}`,
          activity_name: activityName,
          activity_name_en: `${activityName} (English)`,
          activity_description: `${activityName}的詳細描述，包括活動目的、內容和預期成果。`,
          activity_category: category.name,
          activity_date: activityDate,
          start_time: startTime,
          end_time: endTime,
          duration_hours: Math.floor(Math.random() * 4) + 1,
          location: this.getRandomLocation(),
          organizer: this.getRandomOrganizer(),
          participant_count: Math.floor(Math.random() * 50) + 10,
          target_grades: this.getRandomTargetGrades(),
          tenant_id: 'tn_01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    });

    return activities;
  }

  generateMockActivitiesForDateRange(startDate, endDate) {
    const allActivities = this.generateMockActivities();
    return allActivities.filter(activity => 
      activity.activity_date >= startDate && activity.activity_date <= endDate
    );
  }

  generateMockStudentRecords(studentId) {
    const activities = this.generateMockActivities();
    const records = [];
    const achievementLevels = ['優秀', '良好', '合格', '參與'];

    // Generate 3-8 participation records per student
    const recordCount = Math.floor(Math.random() * 6) + 3;
    const selectedActivities = activities.sort(() => 0.5 - Math.random()).slice(0, recordCount);

    selectedActivities.forEach(activity => {
      records.push({
        id: `record_${studentId}_${activity.id}`,
        student_id: studentId,
        activity_id: activity.id,
        participation_date: activity.activity_date,
        achievement_level: achievementLevels[Math.floor(Math.random() * achievementLevels.length)],
        notes: `學生在${activity.activity_name}中表現${Math.random() > 0.5 ? '積極' : '良好'}。`,
        tenant_id: 'tn_01',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        saas_slp_activities: activity
      });
    });

    return records;
  }

  getRandomLocation() {
    const locations = [
      '學校禮堂', '體育館', '圖書館', '實驗室', '美術室', '音樂室',
      '操場', '會議室', '多媒體教室', '戶外活動場地', '社區中心'
    ];
    return locations[Math.floor(Math.random() * locations.length)];
  }

  getRandomOrganizer() {
    const organizers = [
      '學生事務處', '教務處', '體育組', '藝術組', '學生會', '家長教師會',
      '社區服務組', '學術組', '課外活動組', '輔導組'
    ];
    return organizers[Math.floor(Math.random() * organizers.length)];
  }

  getRandomTargetGrades() {
    const grades = ['1', '2', '3', '4', '5', '6'];
    const targetCount = Math.floor(Math.random() * 3) + 1;
    const selectedGrades = grades.sort(() => 0.5 - Math.random()).slice(0, targetCount);
    return selectedGrades;
  }
}

// Create and export service instance
export const slpService = new SlpService();
export { SlpService };
